/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './ClientSideExcelUpload';
import { ItemType } from 'componentregistry/registry/ComponentRegistry';

const ClientSideExcelUploadManager: ComponentManagerFn<
  'ClientSideExcelUpload'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'ClientSideExcelUpload'>(
      platform,
      'ClientSideExcelUpload',
    ),
    type: 'ClientSideExcelUpload',
    name: 'Client Side Excel/CSV Upload',
    isChildAllowed: (page, item, childType) =>
      (['TriggerAfterUpload'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      defaultFunction: {
        label: 'Perform Default Callback Function',
        type: 'controllerFunction',
        alwaysShow: true,
        helpText: `Use this function to populate default values.

This function is called before the data is validated against the datasource.

\`\`\`javascript
import { NewRow, MyDataSource } from "@cloudio-saas/datasource-types";
import type { BaseFunctionProps } from 'cloudio';
import ulid from 'ulid';

function performDefault({ records, fileName }: BaseFunctionProps & { records: NewRow<MyDataSource>[], fileName: string }) {
  const loadUid = ulid();
  records.forEach((record) => {
      for (const [key, value] of Object.entries(record)) {
          if (typeof value === 'string' && value.charAt(0) === '\\'') {
              record[key] = value.substring(1);
          }
      }
      record.loadUid = loadUid;
      record.statusCode = 'P';
  });
  return records;
}
\`\`\`
`,
      },
      callbackFunction: {
        label: 'On Load Callback Function',
        type: 'controllerFunction',
        alwaysShow: true,
        helpText: `Use this function to perform any additional processing.

\`\`\`javascript
import { NewRow, MyDataSource } from "@cloudio-saas/datasource-types";
import type { BaseFunctionProps } from 'cloudio';

function onLoad({ platform, records, fileName }: BaseFunctionProps & { records: NewRow<MyDataSource>[], fileName: string }) {
  const rows = records.filter((record) => record.someAttr === 'someValue');
  const removed = records.length - rows.length;
  if (removed) {
      platform.showWarning(removed === 1 ? \`One record was ignored due to...!\` : \`\${removed} records were ignored due to...!\`);
  }
  return rows;
}
\`\`\`
`,
      },
      alias: {
        label: 'Data Store Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['DataStore'],
      },
      maxRows: {
        label: 'Max Rows Allowed',
        type: 'numberInput',
        required: true,
      },
      dateFormat: {
        label: 'Date Format',
        type: 'string',
        required: true,
        helpText:
          'Date format to be used for parsing dates. Refer to [date-fns](https://date-fns.org/v2.29.3/docs/parse) for more details.',
      },
      dateTimeFormat: {
        label: 'Date Time Format',
        type: 'string',
        required: true,
        helpText:
          'Date time format to be used for parsing date times. Refer to [date-fns](https://date-fns.org/v2.29.3/docs/parse) for more details.',
      },
      headerMappings: {
        label: 'Header Mappings',
        type: 'json',
        required: false,
        sampleData: `[
          {
            "header":  "Sample Header ",
            "attribute": "Sample Attribute Code " 
          }
      ]`,
      },
    },
    getDisplayText: (item) => item.props.callbackFunction,
    category: 'Forms',
    icon: { iconName: 'upload' },
    description: `**Overview**<br/>
The Client Side Excel/CSV Upload component allows users to upload spreadsheets directly into the application.
Unlike server-side uploaders (which upload the raw file to a server for processing), this component parses the file entirely within the user's browser. It converts the rows into data records and inserts them directly into a Data Store. This enables immediate feedback, fast validation, and data manipulation without waiting for a backend job to finish.

**1. Data Binding (Target)**<br/>
You must define where the parsed data should go.
* **Data Store Alias:** Select the Data Store that will receive the records.
* **Mechanism:** The component reads the Excel header row (e.g., "First Name") and attempts to match it to the Data Store attributes.

**2. Mapping & Formatting**<br/>
Spreadsheet data is often "messy" (e.g., weird date formats or mismatched headers). You can configure the parser to handle these inconsistencies.
* **Header Mappings (JSON):** A configuration object to map specific Excel headers to your Data Store attributes if they don't match perfectly. (Example: \`[ { "header": "User Email", "attribute": "email_address" } ]\`).
* **Date / Date Time Format:** Specifies the expected format of date strings in the Excel file (e.g., MM/dd/yyyy). This ensures the parser correctly converts text like "12/01/2023" into a valid Date object.

**3. Processing Logic (Hooks)**<br/>
This component offers two powerful hooks to clean or validate data before it hits the database.
* **A. Perform Default Callback Function (Pre-Validation):** This function runs before the data is validated against the Data Store schema. Use it to clean raw data or generate default values.
    * **Common Use Cases:** Generating a Batch ID (loadUid) for all rows; stripping special characters; setting default status codes.
* **B. On Load Callback Function (Post-Validation):** This function runs after basic parsing is complete but before the final commit to the store. Use it for complex logic or filtering.
    * **Common Use Cases:** Filtering out rows that don't match specific criteria; showing a warning toast to the user.

**4. Constraints**<br/>
* **Max Rows Allowed:** Sets a hard limit on the number of rows a user can upload (e.g., 1000). Since parsing happens in the browser, setting this too high (e.g., 50k+) may freeze the user's tab.

**5. Triggers**<br/>
* **TriggerAfterUpload:** You can add this child component to execute logic after the process is successfully completed (e.g., "Navigate to Review Page" or "Refresh Grid").`,
  };
};

export default ClientSideExcelUploadManager;
